import { FormFieldControl, Input } from "@components";
import { Eye } from "@untitledui/icons";
import type { ReactNode } from "react";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import ModalBase from "@/components/ui/modal/modal-base";
import { OptionDisplayField } from "@/features/questions/components";
import { QuestionOptionDisplayType } from "@/types/question";
import { useChecklistTemplateById } from "../hooks/useChecklistTemplateQueries";
import type { FormViewQuestionDetail } from "../types";
import { DescriptionText } from "./description-text";

type FormViewModalMode = "view" | "duplicate";

type FormViewModalProps = {
	isOpen: boolean;
	onClose: () => void;
	templateId: number | null;
	mode?: FormViewModalMode;
	canDuplicate?: boolean;
	canArchive?: boolean;
	onDuplicate?: () => void;
	onArchive?: () => void;
};

/** optionDisplayType: 1=Icon, 2=Checkbox, 3=Radio, 4=ComboBox/Select, 5=List */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: field covers all question display types
function FormViewQuestionField({
	question,
}: {
	question: FormViewQuestionDetail;
}) {
	const type = question.type ?? 1;
	const optionDisplayType =
		question.optionDisplayType ?? QuestionOptionDisplayType.ComboBox;
	const options = question.options ?? [];
	const sortedOptions = [...options].sort(
		(a, b) => (a.position ?? 0) - (b.position ?? 0)
	);
	const label = question.prompt ?? `Pergunta ${question.questionId}`;
	const isRequired = question.isRequired ?? false;
	const fieldId = `form-view-q-${question.questionId}`;
	const hint = question.description ?? undefined;
	const isMultipleSelection = Boolean(question.isMultipleSelection);

	// Text (type 5)
	if (type === 5) {
		return (
			<FormFieldControl
				className="w-full"
				hint={hint}
				htmlFor={fieldId}
				isRequired={isRequired}
				label={label}
			>
				<Input
					disabled
					id={fieldId}
					placeholder="Digite aqui..."
					readOnly
					value=""
				/>
			</FormFieldControl>
		);
	}

	// Number (type 6)
	if (type === 6) {
		return (
			<FormFieldControl
				className="w-full"
				hint={hint}
				htmlFor={fieldId}
				isRequired={isRequired}
				label={label}
			>
				<Input
					disabled
					id={fieldId}
					placeholder="Digite o número..."
					readOnly
					type="number"
					value=""
				/>
			</FormFieldControl>
		);
	}

	// File (type 7)
	if (type === 7) {
		return (
			<FormFieldControl
				className="w-full"
				hint={hint}
				htmlFor={fieldId}
				isRequired={isRequired}
				label={label}
			>
				<div className="flex min-h-[80px] items-center justify-center rounded-lg border-2 border-gray-300 border-dashed bg-gray-50 py-4 text-gray-500 text-sm">
					Upload de arquivo (somente visualização)
				</div>
			</FormFieldControl>
		);
	}

	// Boolean / Sim-Não (type 8) — checkbox com o título da pergunta
	if (type === 8) {
		return (
			<div className="flex w-full flex-col gap-1.5">
				<Checkbox
					isDisabled
					isSelected={false}
					label={
						<span className="inline-flex items-start gap-1 text-gray-700 text-sm">
							<span>{label}</span>
							{isRequired ? (
								<span className="shrink-0 text-brand-tertiary">*</span>
							) : null}
						</span>
					}
				/>
				{hint ? <p className="text-gray-500 text-sm">{hint}</p> : null}
			</div>
		);
	}

	// Icon (optionDisplayType 1)
	if (optionDisplayType === QuestionOptionDisplayType.Icon) {
		return (
			<FormFieldControl
				className="w-full"
				hint={hint}
				htmlFor={fieldId}
				isRequired={isRequired}
				label={label}
			>
				<div className="flex flex-wrap gap-2">
					{sortedOptions.map((op) => (
						<div
							className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600 text-sm"
							key={op.id}
						>
							{op.label}
						</div>
					))}
				</div>
			</FormFieldControl>
		);
	}

	// Checkbox / Radio / ComboBox / List
	if (
		optionDisplayType === QuestionOptionDisplayType.Checkbox ||
		optionDisplayType === QuestionOptionDisplayType.Radio ||
		optionDisplayType === QuestionOptionDisplayType.ComboBox ||
		optionDisplayType === QuestionOptionDisplayType.List
	) {
		return (
			<FormFieldControl
				className="w-full"
				hint={hint}
				htmlFor={fieldId}
				isRequired={isRequired}
				label={label}
			>
				<OptionDisplayField
					ariaLabel={label}
					isDisabled
					isMultipleSelection={isMultipleSelection}
					optionDisplayType={optionDisplayType}
					options={sortedOptions}
					value={isMultipleSelection ? [] : ""}
				/>
			</FormFieldControl>
		);
	}

	return (
		<FormFieldControl
			className="w-full"
			hint={hint}
			htmlFor={fieldId}
			isRequired={isRequired}
			label={label}
		>
			<Input disabled id={fieldId} placeholder="—" readOnly value="" />
		</FormFieldControl>
	);
}

export const FormViewModal = ({
	isOpen,
	onClose,
	templateId,
	mode = "view",
	canDuplicate = false,
	canArchive = false,
	onDuplicate,
	onArchive,
}: FormViewModalProps) => {
	const {
		data: template,
		isError,
		isLoading,
	} = useChecklistTemplateById(templateId, { enabled: isOpen });

	const sections = template?.sections ?? [];
	const sortedSections = [...sections].sort(
		(a, b) => (a.position ?? 0) - (b.position ?? 0)
	);

	let modalContent: ReactNode;
	if (!templateId || Number.isNaN(templateId)) {
		modalContent = (
			<p className="text-gray-600 text-sm">ID do formulário inválido.</p>
		);
	} else if (isLoading) {
		modalContent = (
			<p className="py-8 text-center text-gray-600 text-sm">
				Carregando formulário...
			</p>
		);
	} else if (isError || !template) {
		modalContent = (
			<p className="py-8 text-center text-gray-600 text-sm">
				Não foi possível carregar o formulário. Tente novamente.
			</p>
		);
	} else {
		modalContent = (
			<>
				<div className="border-gray-100 border-b pb-4">
					<h2 className="font-semibold text-gray-900 text-lg">
						{template.name}
					</h2>
					{template.description ? (
						<DescriptionText className="mt-1">
							{template.description}
						</DescriptionText>
					) : null}
				</div>

				<div className="mt-4 mb-4 space-y-6">
					{sortedSections.length === 0 ? (
						<p className="text-gray-500 text-sm">
							Nenhuma seção cadastrada neste formulário.
						</p>
					) : (
						sortedSections.map((section, sectionIndex) => {
							const questions = [...(section.questions ?? [])].sort(
								(a, b) => (a.position ?? 0) - (b.position ?? 0)
							);
							return (
								<div
									className="border-gray-100 border-b pb-5 last:border-b-0 last:pb-0"
									key={`${section.id ?? sectionIndex}-${section.name}`}
								>
									<h3 className="mb-1 font-bold text-base text-gray-800">
										{sectionIndex + 1}. {section.name}
									</h3>
									{section.description ? (
										<DescriptionText className="mb-3">
											{section.description}
										</DescriptionText>
									) : (
										<div className="mb-3" />
									)}
									<div className="flex flex-col gap-4">
										{questions.map((q, qIndex) => (
											<FormViewQuestionField
												key={`${q.id ?? q.questionId}-${qIndex}`}
												question={q as FormViewQuestionDetail}
											/>
										))}
									</div>
								</div>
							);
						})
					)}
				</div>
			</>
		);
	}

	const duplicateLabel =
		mode === "duplicate" ? "Confirmar Duplicação" : "Duplicar";

	const actions = (() => {
		const modalActions: {
			label: string;
			onClick?: () => void;
			color?: "primary" | "secondary";
		}[] = [];

		if (canArchive) {
			modalActions.push({
				label: "Arquivar",
				color: "secondary",
				onClick: onArchive,
			});
		} else {
			modalActions.push({
				label: "Fechar",
				color: "secondary",
				onClick: onClose,
			});
		}

		if (canDuplicate) {
			modalActions.push({
				label: duplicateLabel,
				color: "primary",
				onClick: onDuplicate,
			});
		}

		return modalActions;
	})();

	return (
		<ModalBase
			actions={actions}
			description="Apenas visualização. Nenhuma alteração é permitida."
			height="auto"
			icon={<Eye className="size-6" />}
			iconColor="brand"
			isOpen={isOpen}
			onClose={onClose}
			title="Visualizar formulário"
			width={912}
		>
			<div className="max-h-[50vh] min-h-0 overflow-y-auto pr-1">
				{modalContent}
			</div>
		</ModalBase>
	);
};
